import React, { useEffect, useRef } from "react";
import { Button, Loader } from "@mantine/core";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";

interface PrintWindow extends Window {
  ready?: boolean;
}

export default function ReportPrintButton(props) {
  const { t } = useTranslation();

  const [printReady, setPrintReady] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    setPrintReady(false);
    if (props.componentRefs.length > 0) {
      setPrintReady(true);
    }
    return () => {
      if (iframeRef.current) {
        document.body.removeChild(iframeRef.current);
      }
    };
  }, [props.componentRefs]);

  const captureImages = async (refs) => {
    const root = document.documentElement;
    const prev = root.getAttribute("data-mantine-color-scheme");
    root.setAttribute("data-mantine-color-scheme", "light");

    const images = [];
    for (const ref of refs) {
      if (ref) {
        try {
          const canvas = await html2canvas(ref, {
            scale: 2,
            useCORS: true,
            logging: false,
          });
          const imgData = canvas.toDataURL("image/png");
          images.push(imgData);
        } catch (err) {
          console.error("Failed to capture image:", err);
        }
      }
    }

    if (prev) root.setAttribute("data-mantine-color-scheme", prev);
    else root.removeAttribute("data-mantine-color-scheme");

    return images;
  };

  const createPrintFrame = async () => {
    try {
      setLoading(true);
      const images = await captureImages(props.componentRefs);

      if (!iframeRef.current) {
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        document.body.appendChild(iframe);
        iframeRef.current = iframe;
      }

      const doc = iframeRef.current?.contentDocument;
      if (!doc) throw new Error("Could not access iframe document");

      // Write content and wait for images to load
      await new Promise((resolve) => {
        doc.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <style>
                @media print {
                  @page {
                    size: A4;
                    margin: 0;
                  }
                  body {
                    margin: 0;
                    padding: 0;
                  }
                  .page-container {
                    width: 210mm;
                    height: 297mm;
                    page-break-after: always;
                    position: relative;
                    overflow: hidden;
                  }
                  img {
                    display: block;
                    width: 100%;
                    max-height: 297mm;
                    object-fit: contain;
                    margin: 0;
                    padding: 0;
                  }
                  img:first-child {
                    page-break-before: avoid;
                  }
                }
              </style>
            </head>
            <body>
              ${images
                .map(
                  (img, i) => `
                <div class="page-container">
                  <img 
                    src="${img}" 
                    onload="${i === images.length - 1 ? "window.ready=true" : ""}"
                  />
                </div>
              `,
                )
                .join("")}
            </body>
          </html>
        `);
        doc.close();

        // Wait for all images to load
        const checkReady = setInterval(() => {
          const printWindow = doc.defaultView as PrintWindow;
          if (printWindow?.ready) {
            clearInterval(checkReady);
            resolve(true);
          }
        }, 100);
      });

      // Trigger print
      iframeRef.current.contentWindow?.print();
    } catch (err) {
      console.error("Print failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button onClick={createPrintFrame} size="compact-md" px={"sm"} disabled={!printReady}>
      {loading ? <Loader color={"white"} size={20} /> : t("ResultList.print")}
    </Button>
  );
}
